/**
 * The health strip, and the forge it opens.
 *
 * The strip is one line and is always present: when everything is healthy it says so, because an
 * absent warning and a broken watchdog look identical otherwise.
 *
 * The forge is the armourer's workshop rather than the ship: the place a broken thing is taken to
 * be looked at. Internal surface, so the franchise skin applies. Its whole purpose is the copy
 * control, so the brief gets a recessed housing and everything else stays quiet.
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import type { AssuranceView, LaneHealth } from '@rbv/shared';

/**
 * How long a lamp stays lit after its lane reports.
 *
 * Long enough to catch out of the corner of an eye, short enough that two lanes reporting a minute
 * apart read as two events rather than one continuous state.
 */
const FLASH_MS = 6000;

/** Age in the shortest form that is still true. Ticks, so the board is never frozen. */
function ago(iso: string, now: number): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

/**
 * The lane rail: one lamp per lane, and nothing on it is decorative.
 *
 * HIS REPORT, 2026-08-31, looking at the deck full screen on an ultrawide: "it looks dead, nothing
 * is moving, no signal". He is right, and the fix is not to animate something arbitrary. This
 * board's whole claim is that it never shows anything it cannot back, so a blinking light that
 * means nothing would be the worst possible thing to put on it.
 *
 * So the rail carries TWO honest dimensions and no third:
 *
 *   COLOUR is state. Blue is holding, amber is degraded, red is quarantined. The same three the
 *   strip used, and the same ones the forge opens on.
 *
 *   MOTION is freshness. A lamp FLASHES when its lane actually reports, detected by the report
 *   timestamp changing between polls. Watch the board for a few minutes and the mail sweep, the
 *   calendar poll and the publishing sweep light up as they run, because they really are running.
 *
 * A quarantined lane is deliberately STILL. Everything else breathes slowly, so stillness reads as
 * dead, which is exactly what quarantined means. The age counter ticks every second, so the panel
 * is never frozen even between reports.
 *
 * Clicking a faulty lane still opens the forge. That was the whole point of the old strip and it
 * survives unchanged.
 */
export function HealthStrip({ view }: { view: AssuranceView }): JSX.Element {
  const [open, setOpen] = useState<LaneHealth | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** Lane to the moment we noticed it report. Drives the flash, not the render otherwise. */
  const [lit, setLit] = useState<Record<string, number>>({});
  const seen = useRef<Record<string, string>>({});

  // One second, so the ages tick. Cheap: it re-renders a row of spans.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /*
   * A lane counts as having reported when its `last.at` CHANGES, not when it is recent. Recency
   * would light every lane on first paint and then never again, which is the opposite of alive.
   */
  useEffect(() => {
    const fresh: Record<string, number> = {};
    for (const l of view.lanes) {
      const at = l.last?.at ?? '';
      if (at !== '' && seen.current[l.lane] !== undefined && seen.current[l.lane] !== at) {
        fresh[l.lane] = Date.now();
      }
      if (at !== '') seen.current[l.lane] = at;
    }
    if (Object.keys(fresh).length > 0) setLit((p) => ({ ...p, ...fresh }));
  }, [view]);

  const faults = view.lanes.filter((l) => l.state !== 'healthy');
  const worst = faults.find((f) => f.state === 'quarantined') ?? faults[0];

  return (
    <>
      <div className="cdeck-rail-lanes" role="group" aria-label="Lane telemetry">
        {view.lanes.map((l) => {
          const at = l.last?.at;
          const flashing = lit[l.lane] !== undefined && now - lit[l.lane] < FLASH_MS;
          const label = `${l.label}: ${l.state}${at ? `, last reported ${ago(at, now)} ago` : ', never reported'}`;
          const body = (
            <>
              <span className="cdeck-lamp" data-state={l.state} data-lit={flashing ? 'yes' : 'no'} />
              <span className="cdeck-lamp-name">{l.label}</span>
              <span className="cdeck-lamp-age">{at ? ago(at, now) : 'never'}</span>
            </>
          );

          /* Only a faulty lane is a button: a healthy one has no brief to open, and a control that
             does nothing is worse than plain text. */
          return l.state === 'healthy' ? (
            <span key={l.lane} className="cdeck-lane" title={label} aria-label={label}>
              {body}
            </span>
          ) : (
            <button
              key={l.lane}
              type="button"
              className="cdeck-lane"
              data-fault="yes"
              title={label}
              aria-label={`${label}. Open the fault brief.`}
              onClick={() => setOpen(l)}
            >
              {body}
            </button>
          );
        })}

        <span className="cdeck-lane-verdict" data-state={view.allHealthy ? 'healthy' : worst.state}>
          {view.allHealthy ? 'All lanes holding' : `${worst.label}: ${worst.why}`}
        </span>
      </div>
      {open !== null && <Forge lane={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function Forge({ lane, onClose }: { lane: LaneHealth; onClose: () => void }): JSX.Element {
  const [brief, setBrief] = useState('');
  const [copied, setCopied] = useState(false);

  /* In an effect, not in the render body: a fetch during render re-renders on resolve and
     fetches again, forever. Keyed on the lane so opening a different fault refetches. */
  useEffect(() => {
    let live = true;
    void fetch(`/api/mo/assurance/${lane.lane}/brief`)
      .then((r) => r.text())
      .then((t) => { if (live) setBrief(t); })
      .catch(() => { if (live) setBrief('Could not build the brief. The fault above still stands.'); });
    return () => { live = false; };
  }, [lane.lane]);

  return (
    <div className="cdeck-forge-wrap" role="dialog" aria-label={`${lane.label} fault`}>
      <div className="cdeck-forge">
        <div className="cdeck-forge-head">
          <span className="cdeck-forge-ingot" data-state={lane.state}>{lane.label}</span>
          <span className="cdeck-forge-state">{lane.state}</span>
          <button type="button" className="cdeck-forge-close" onClick={onClose}>Close</button>
        </div>
        <p className="cdeck-forge-why">{lane.why}</p>
        <div className="cdeck-forge-brief">
          <pre>{brief === '' ? 'Building the brief…' : brief}</pre>
        </div>
        <button
          type="button"
          className="cdeck-forge-copy"
          onClick={() => {
            void navigator.clipboard.writeText(brief).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy the brief for Claude'}
        </button>
      </div>
    </div>
  );
}
